import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';

import { switchMap } from 'rxjs';

import { RecaptchaService } from '@services/recaptcha/recaptcha.service';

/**
 * Public endpoints guarded by `RecaptchaGuard` in the backend. Closed list on purpose:
 * no other request should depend on Google's script loading. Notably
 * `PATCH /orders/:id/status` and `POST /orders/:id/payment/init` are NOT guarded.
 */
const RECAPTCHA_ENDPOINTS: readonly RegExp[] = [
  /\/api\/mailing\/custom-design-request(\?|$)/,
  /\/api\/mailing\/merchandising-quote-request(\?|$)/,
  /\/api\/orders(\?|$)/,
  /\/api\/shipping\/quote(\?|$)/,
];

function needsRecaptcha(req: HttpRequest<unknown>): boolean {
  return req.method === 'POST' && RECAPTCHA_ENDPOINTS.some((endpoint) => endpoint.test(req.url));
}

export const recaptchaInterceptor: HttpInterceptorFn = (req, next) => {
  if (!needsRecaptcha(req)) {
    return next(req);
  }

  const recaptchaService = inject(RecaptchaService);

  return recaptchaService.executeAction('api_request').pipe(
    switchMap((token) => {
      // Send anyway without a token: the backend answers 403 and the UI shows a
      // verification error, which beats leaving the customer waiting forever.
      if (!token) {
        return next(req);
      }

      return next(req.clone({ setHeaders: { 'X-Recaptcha-Token': token } }));
    }),
  );
};
